import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { components } from "@/api/schema";

import { stockKeys } from "./query-keys";

type Stock = components["schemas"]["Stock"];

export interface UpdateStockVars {
  sku: string;
  mkp: string;
  body: {
    multiplier?: string;
    fake?: number;
    lock?: number;
    /**
     * Optional operator note (PRD v1.8 §4.2) recorded on the audit log for
     * context. Audit-only — never merged into the cached Stock row. Only sent
     * when non-empty; the backend treats absent/empty as "store nothing".
     */
    remark?: string;
  };
}

export function useUpdateStock() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ sku, mkp, body }: UpdateStockVars) => {
      const { remark, ...fields } = body;
      const trimmed = remark?.trim();
      const { data, error } = await apiClient.PATCH("/v1/stock/{sku}/{mkp}", {
        params: { path: { sku, mkp: mkp as components["parameters"]["Marketplace"] } },
        body: { ...fields, ...(trimmed ? { remark: trimmed } : {}) },
      });
      if (error) throw error;
      return data;
    },
    onMutate: async ({ sku, mkp, body }) => {
      await qc.cancelQueries({ queryKey: stockKeys.detail(sku, mkp) });
      const previous = qc.getQueryData<Stock>(stockKeys.detail(sku, mkp));
      // `remark` is audit context, not a stock field — exclude it from the
      // optimistic merge so it never leaks into cached row state.
      const { remark: _remark, ...fields } = body;
      qc.setQueryData<Stock>(stockKeys.detail(sku, mkp), (old) =>
        old ? { ...old, ...fields } : old,
      );
      return { previous };
    },
    onError: (_err, { sku, mkp }, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(stockKeys.detail(sku, mkp), ctx.previous);
      }
    },
    onSettled: (_data, _err, { sku, mkp }) => {
      void qc.invalidateQueries({ queryKey: stockKeys.detail(sku, mkp) });
      void qc.invalidateQueries({ queryKey: stockKeys.lists });
      void qc.invalidateQueries({ queryKey: stockKeys.audit(sku, mkp) });
    },
  });
}
